import { JsonLd } from 'react-schemaorg'
import { Brand, CollectionPage, ItemList, WithContext } from 'schema-dts'

import config from 'src/config'
import magazineUrl from 'src/functions/magazineUrl'
import { hrefForBrandPath } from 'src/functions/navigationBreadcrumbs'
import { productVariantUrl } from 'src/functions/productUrl'
import { tBrands } from 'src/shared/frontendTypes/brand'
import { tProductMagazineReference, tProductPreview } from 'src/shared/frontendTypes/product'
import shopInfos from 'template/infos'

type CatalogPageType = 'menu' | 'brand' | 'brandMenu'

interface Props {
  pageType?: CatalogPageType
  title?: string
  description?: string
  metaTitle?: string
  metaDescription?: string
  canonicalUrl?: string
  brandLogoSrc?: string
  products?: tProductPreview[]
  brands?: tBrands
  magazines?: tProductMagazineReference[]
}

const shopUrl = shopInfos.url.replace(/\/$/, '')

const cleanText = (value?: string) =>
  String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const absoluteUrl = (href?: string) => {
  const trimmed = String(href || '').trim()
  if (!trimmed) return undefined
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `${shopUrl}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`
}

const absoluteImageUrl = (src?: string) => {
  const trimmed = String(src || '').trim()
  if (!trimmed) return undefined
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  if (trimmed.startsWith('/')) return `${shopUrl}${trimmed}`
  return `${config.image_server}/${trimmed}`
}

const resolveBrandListLogo = (brand: tBrands[number]) => {
  const logo = brand.logos?.color || brand.logos?.mono
  return logo?.sm ? absoluteImageUrl(`brand/${logo.sm}`) : undefined
}

const buildListItems = ({
  products = [],
  brands = [],
  magazines = [],
}: Pick<Props, 'products' | 'brands' | 'magazines'>) => {
  const items: Array<Record<string, unknown>> = []

  products.slice(0, 48).forEach(product => {
    const url = absoluteUrl(productVariantUrl(product.id, product.path, product.key))
    const name = cleanText(product.metaTitle || product.name)
    if (!url || !name) return

    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name,
      url,
    })
  })

  magazines.slice(0, 24).forEach(magazine => {
    const url = absoluteUrl(magazine.href || magazineUrl(magazine.id, magazine.slug, magazine.pageIndex))
    const name = cleanText(magazine.title)
    if (!url || !name) return

    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name,
      url,
    })
  })

  brands.slice(0, 24).forEach(brand => {
    const url = absoluteUrl(hrefForBrandPath(brand.path, brand.id))
    const name = cleanText(brand.metaTitle || brand.titel)
    if (!url || !name) return

    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name,
      url,
      image: resolveBrandListLogo(brand),
    })
  })

  return items
}

const FilterStructuredData: React.FC<Props> = ({
  pageType,
  title,
  description,
  metaTitle,
  metaDescription,
  canonicalUrl,
  brandLogoSrc,
  products,
  brands,
  magazines,
}) => {
  if (!pageType) return null

  const url = absoluteUrl(canonicalUrl) || shopUrl
  const name = cleanText(metaTitle || title || shopInfos.name)
  const descriptionText = cleanText(metaDescription || description || shopInfos.description)
  const pageId = `${url}#webpage`
  const listId = `${url}#itemlist`
  const brandId = `${url}#brand`
  const listItems = buildListItems({ products, brands, magazines })
  const itemList = listItems.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        '@id': listId,
        itemListElement: listItems,
      } as unknown as WithContext<ItemList>
    : null
  const brand = pageType === 'brand'
    ? {
        '@context': 'https://schema.org',
        '@type': 'Brand',
        '@id': brandId,
        name,
        url,
        ...(descriptionText ? { description: descriptionText } : {}),
        ...(absoluteImageUrl(brandLogoSrc) ? { logo: absoluteImageUrl(brandLogoSrc) } : {}),
      } as WithContext<Brand>
    : null
  const collectionPage = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': pageId,
    url,
    name,
    ...(descriptionText ? { description: descriptionText } : {}),
    isPartOf: { '@id': `${shopUrl}#website` },
    inLanguage: 'de-DE',
    ...(itemList ? { mainEntity: { '@id': listId } } : {}),
    ...(brand ? { about: { '@id': brandId } } : {}),
  } as WithContext<CollectionPage>

  return <>
    <JsonLd<CollectionPage> item={collectionPage} />
    {brand && <JsonLd<Brand> item={brand} />}
    {itemList && <JsonLd<ItemList> item={itemList} />}
  </>
}

export default FilterStructuredData
